import {Injectable} from '@angular/core';
import {deepEqual, isNonNull, TranslationService} from '@emma-labs/shared/angular';
import {
  buildProtectionSummary,
  calculateAgeTruncated,
  DocumentName,
  extractCoverageFromProtection,
  ExtraRequirementsInquiry,
  getLifeInsuranceProvider,
  getPresetRevisions,
  HealthAnswersRevisionType,
  ModificationNoticeBreakdown,
  Protection,
  PublicProfile,
  QuestionId,
  QuestionnaireId,
  TextTranslations,
  TranslatedUiQuestion,
  UnderwritingProfile,
  UnderwritingProfileNote,
  UserRole,
} from '@emma-labs/shared/typescript';
import {
  BehaviorSubject,
  distinctUntilChanged,
  filter,
  first,
  forkJoin,
  Observable,
  of,
  switchMap,
  tap,
} from 'rxjs';
import {map, mergeMap} from 'rxjs/operators';
import {ExtraQuestionnaire} from '../../entities/archived-proposal-object/archived-proposal-page-object';
import {ProtectionSummary} from '../../entities/underwriting-page-object/profile-info';
import {
  EnrichedProfileNote,
  UnderwritingProfilePageObject,
} from '../../entities/underwriting-page-object/underwriting-page-object';
import {AgentService} from '../agent.service';
import {HealthQuestionnaireService} from '../health-questionnaire.service';
import {buildClientInfoObject, buildStatusObject, combineExtraQuestionnaires} from '../orchestrator-utils';
import {ArchivedProposalService} from '../proposal/archived-proposal.service';
import {ProtectionService} from '../protection.service';
import {UnderwritingProfileService} from './underwriting-profile.service';

@Injectable({
  providedIn: 'root',
})
export class UnderwritingProfilePageOrchestrator {
  private readonly _store$: BehaviorSubject<UnderwritingProfilePageObject | undefined>;
  private readonly store$: Observable<UnderwritingProfilePageObject>;

  constructor(
    private readonly underwritingProfileService: UnderwritingProfileService,
    private readonly archivedProposalService: ArchivedProposalService,
    private readonly translate: TranslationService,
    private readonly agentService: AgentService,
    private readonly protectionService: ProtectionService,
    private readonly healthQuestionnaireService: HealthQuestionnaireService,
  ) {
    this._store$ = new BehaviorSubject<UnderwritingProfilePageObject | undefined>(undefined);
    this.store$ = this._store$.pipe(filter(isNonNull), distinctUntilChanged(deepEqual));
  }

  get(underwritingProfileId: string): Observable<UnderwritingProfilePageObject> {
    const currentValue = this._store$.getValue();

    if (!currentValue || currentValue.id !== underwritingProfileId) {
      this.clear();
      this.fetch(underwritingProfileId);
    }

    return this.store$;
  }

  saveNote(text: string): Observable<UnderwritingProfilePageObject> {
    return this.underwritingProfileService.saveNote(this.getUnderwritingProfileId(), text).pipe(
      switchMap((underwritingProfile) => this.getUnderwritingProfilePageObject(underwritingProfile)),
      tap((underwritingProfilePageObject) => this._store$.next(underwritingProfilePageObject)),
    );
  }

  updateIsIncludedInContract(
    isIncludedInContract: boolean,
    healthAnswersRevisionId: string,
  ): Observable<undefined> {
    const underwritingProfileId = this.getUnderwritingProfileId();
    return this.underwritingProfileService.updateIsIncludedInContract(
      underwritingProfileId,
      isIncludedInContract,
      healthAnswersRevisionId,
    );
  }

  createHealthAnswersRevision(output: QuestionnaireId[] | string): Observable<UnderwritingProfilePageObject> {
    const healthAnswersRevision =
      typeof output === 'string'
        ? {
            type: HealthAnswersRevisionType.CUSTOM,
            revision: {question: output},
          }
        : {
            type: HealthAnswersRevisionType.PRESET,
            revision: {questionnaires: output.map((questionnaireId) => ({questionnaireId}))},
          };

    return this.underwritingProfileService
      .createHealthAnswersRevision(this.getUnderwritingProfileId(), healthAnswersRevision)
      .pipe(
        switchMap((underwritingProfile) => {
          return this.getUnderwritingProfilePageObject(underwritingProfile);
        }),
        tap((underwritingProfilePageObject) => this._store$.next(underwritingProfilePageObject)),
      );
  }

  submitExtraRequirements(
    extraRequirementsInquiry: Omit<ExtraRequirementsInquiry, 'id' | 'createdAt'>,
  ): Observable<UnderwritingProfilePageObject> {
    return this.underwritingProfileService
      .submitExtraRequirements(this.getUnderwritingProfileId(), extraRequirementsInquiry)
      .pipe(
        switchMap((underwritingProfile) => this.getUnderwritingProfilePageObject(underwritingProfile)),
        tap((underwritingProfilePageObject) => this._store$.next(underwritingProfilePageObject)),
      );
  }

  updateSurchargeRate(surchargeRate: number | null): Observable<void> {
    return this.underwritingProfileService
      .updateSurchargeRate(this.getUnderwritingProfileId(), surchargeRate)
      .pipe(
        switchMap((underwritingProfile) => this.getUnderwritingProfilePageObject(underwritingProfile)),
        tap((underwritingProfilePageObject) => this._store$.next(underwritingProfilePageObject)),
        map(() => undefined),
      );
  }

  refuseProfile(refusalMessage: TextTranslations): Observable<void> {
    return this.underwritingProfileService.refuse(this.getUnderwritingProfileId(), refusalMessage).pipe(
      switchMap((underwritingProfile) => this.getUnderwritingProfilePageObject(underwritingProfile)),
      tap((underwritingProfilePageObject) => this._store$.next(underwritingProfilePageObject)),
      map(() => undefined),
    );
  }

  approveProfile(modificationNoticeBreakdown?: ModificationNoticeBreakdown): Observable<void> {
    return this.underwritingProfileService
      .approve(this.getUnderwritingProfileId(), modificationNoticeBreakdown)
      .pipe(
        switchMap((underwritingProfile) => this.getUnderwritingProfilePageObject(underwritingProfile)),
        tap((underwritingProfilePageObject) => this._store$.next(underwritingProfilePageObject)),
        map(() => undefined),
      );
  }

  cancelProfile(): Observable<void> {
    return this.underwritingProfileService.cancel(this.getUnderwritingProfileId()).pipe(
      switchMap((underwritingProfile) => this.getUnderwritingProfilePageObject(underwritingProfile)),
      tap((underwritingProfilePageObject) => this._store$.next(underwritingProfilePageObject)),
      map(() => undefined),
    );
  }

  getExtraRequirementsDocument(documentName: string): Observable<Blob> {
    return this.underwritingProfileService.getExtraRequirementsDocument(
      this.getUnderwritingProfileId(),
      documentName,
    );
  }

  removeExtraRequirementsDocument(documentName: string): Observable<void> {
    return this.underwritingProfileService
      .removeExtraRequirementsDocument(this.getUnderwritingProfileId(), documentName)
      .pipe(
        switchMap((underwritingProfile) => this.getUnderwritingProfilePageObject(underwritingProfile)),
        tap((underwritingProfilePageObject) => this._store$.next(underwritingProfilePageObject)),
        map(() => undefined),
      );
  }

  uploadExtraRequirementsDocuments(documents: File[]): Observable<void> {
    return this.underwritingProfileService
      .uploadExtraRequirementsDocuments(this.getUnderwritingProfileId(), documents)
      .pipe(
        switchMap((underwritingProfile) => this.getUnderwritingProfilePageObject(underwritingProfile)),
        tap((underwritingProfilePageObject) => this._store$.next(underwritingProfilePageObject)),
        map(() => undefined),
      );
  }

  getHealthAnswersRevisionDocument(healthAnswersRevisionId: string, documentName: string): Observable<Blob> {
    return this.get(this.getUnderwritingProfileId()).pipe(
      first(),
      switchMap((underwritingProfile) =>
        this.archivedProposalService.getHealthAnswersRevisionDocument(
          underwritingProfile.archivedProposalId,
          healthAnswersRevisionId,
          documentName,
        ),
      ),
    );
  }

  saveUnderwriter(agentId?: string): Observable<void> {
    return this.underwritingProfileService.saveUnderwriter(this.getUnderwritingProfileId(), agentId).pipe(
      switchMap((underwritingProfile) => this.getUnderwritingProfilePageObject(underwritingProfile)),
      tap((underwritingProfilePageObject) => this._store$.next(underwritingProfilePageObject)),
      map(() => undefined),
    );
  }

  getReplacementNoticeDocument(): Observable<Blob> {
    return this.underwritingProfileService.getDocument(
      this.getUnderwritingProfileId(),
      DocumentName.REPLACEMENT_NOTICE,
    );
  }

  clear() {
    this._store$.next(undefined);
  }

  private fetch(underwritingProfileId: string): void {
    this.underwritingProfileService
      .get(underwritingProfileId)
      .pipe(
        first(),
        switchMap((underwritingProfile) => this.getUnderwritingProfilePageObject(underwritingProfile)),
      )
      .subscribe((pageObject) => this._store$.next(pageObject));
  }

  private getUnderwritingProfilePageObject(
    underwritingProfile: UnderwritingProfile,
  ): Observable<UnderwritingProfilePageObject> {
    return forkJoin([
      this.enrichNotes(underwritingProfile.notes),
      this.healthQuestionnaireService.getQuestions(
        underwritingProfile.archivedProposal.providerId,
        underwritingProfile.archivedProposal.providerVersion.healthQuestionnaireVersion,
        underwritingProfile.archivedProposal.healthAnswers,
      ),
      underwritingProfile.archivedProposal.healthAnswersRevisions &&
      underwritingProfile.archivedProposal.healthAnswersRevisions.length > 0
        ? this.healthQuestionnaireService.getExtraQuestionnaires(
            underwritingProfile.archivedProposal.providerId,
            underwritingProfile.archivedProposal.providerVersion.healthQuestionnaireVersion,
            getPresetRevisions(underwritingProfile.archivedProposal.healthAnswersRevisions).flatMap(
              (revision) => {
                return revision.questionnaires.map((q) => ({
                  ...q,
                  healthAnswers: revision.answers ?? {},
                }));
              },
            ),
          )
        : of(undefined),
      this.healthQuestionnaireService.getAllExtraQuestionnaires(
        underwritingProfile.archivedProposal.providerId,
        underwritingProfile.archivedProposal.providerVersion.healthQuestionnaireVersion,
      ),
      this.getSurchargedProtection(underwritingProfile),
      this.agentService.getPublicProfilesByRoles([UserRole.UNDERWRITER, UserRole.CASE_MANAGER]),
    ]).pipe(
      map(
        ([
          enrichedNotes,
          healthQuestionnaire,
          revisionsExtraQuestionnaires,
          requestableExtraQuestionnaires,
          surchargedProtection,
          publicProfiles,
        ]) => {
          const extraQuestionnaires = combineExtraQuestionnaires(
            this.translate.currentLanguageIsFr(),
            underwritingProfile.archivedProposal,
            healthQuestionnaire.extraQuestionnaires,
            revisionsExtraQuestionnaires,
          );

          // We remove the answers for EQs that are not marked as complete
          extraQuestionnaires.forEach((eq) => {
            if (!eq.completedAt) {
              eq.presetQuestions?.forEach(({answer}) => {
                answer.value = undefined;
                answer.placeholder = this.translate.currentLanguageIsFr()
                  ? 'En attente de la réponse...'
                  : 'Awaiting answer...';
              });
              if (eq.customQuestion) {
                eq.customQuestion.answer = undefined;
                eq.customQuestion.documentNames = undefined;
              }
            }
          });

          return this.buildUnderwritingProfilePageObject(
            underwritingProfile,
            enrichedNotes,
            {...healthQuestionnaire, extraQuestionnaires},
            requestableExtraQuestionnaires,
            publicProfiles,
            surchargedProtection,
          );
        },
      ),
    );
  }

  private getUnderwritingProfileId(): string {
    const storeValue = this._store$.getValue();
    if (!storeValue) {
      throw new Error('Cannot update an underwriting profile that was never fetched.');
    }

    return storeValue.id;
  }

  private enrichNotes(notes: UnderwritingProfileNote[]): Observable<EnrichedProfileNote[]> {
    const agentNotes = notes.filter((note) => !!note.agentId);
    const systemNotes = notes.filter((note) => !note.agentId);
    return this.agentService.getAgentsFromIds(agentNotes.map((note) => note.agentId!)).pipe(
      map((agents) => {
        const agentMap = new Map(agents.map((agent) => [agent.id, agent.fullName]));
        if (agentNotes.some((note) => !agentMap.has(note.agentId!))) {
          throw new Error('Some notes are missing agent information');
        }
        return agentNotes.map((note) => ({
          ...note,
          agentName: agentMap.get(note.agentId!)!,
        }));
      }),
      map((enrichedAgentNotes) => [...systemNotes, ...enrichedAgentNotes]),
    );
  }

  private getSurchargedProtection(
    underwritingProfile: UnderwritingProfile,
  ): Observable<Protection | undefined> {
    if (!underwritingProfile.surchargeRate) {
      return of(undefined);
    }

    return this.healthQuestionnaireService
      .getCategoryCode(
        underwritingProfile.archivedProposal.providerId,
        underwritingProfile.archivedProposal.providerVersion.healthQuestionnaireVersion,
        underwritingProfile.archivedProposal.healthAnswers,
      )
      .pipe(
        mergeMap((categoryCode) => {
          if (!categoryCode) {
            throw new Error('Could not retrieve category code');
          }

          return this.protectionService.getSurchargedProtection(
            underwritingProfile.archivedProposal.providerVersion.ratesVersion,
            extractCoverageFromProtection(underwritingProfile.archivedProposal.protection),
            categoryCode,
            underwritingProfile.surchargeRate!,
            calculateAgeTruncated(
              underwritingProfile.archivedProposal.healthAnswers.birthday,
              underwritingProfile.archivedProposal.submittedAt,
            ),
            underwritingProfile.archivedProposal.healthAnswers.smoker!,
            underwritingProfile.archivedProposal.healthAnswers.sex,
            underwritingProfile.archivedProposal.jobCategoryId
              ? getLifeInsuranceProvider(underwritingProfile.archivedProposal.providerId)
                  .jobCategoryIdToRiskClass?.[underwritingProfile.archivedProposal.jobCategoryId]
              : undefined,
          );
        }),
      );
  }

  private buildUnderwritingProfilePageObject(
    underwritingProfile: UnderwritingProfile,
    enrichedNotes: EnrichedProfileNote[],
    healthQuestionnaire: {
      questions: TranslatedUiQuestion[];
      extraQuestionnaires: ExtraQuestionnaire[];
    },
    requestableExtraQuestionnaire: {
      extraQuestionnaires: {
        id: QuestionnaireId;
        questions: TranslatedUiQuestion[];
        status: string;
        title: string;
      }[];
    },
    publicProfiles: PublicProfile[],
    surchargedProtection?: Protection,
  ): UnderwritingProfilePageObject {
    const insuree = underwritingProfile.archivedProposal.insuree;
    const healthAnswers = underwritingProfile.archivedProposal.healthAnswers;
    const protection = underwritingProfile.archivedProposal.protection;

    if (!healthAnswers || !healthAnswers.birthday || !insuree || !insuree.languageCode || !protection) {
      throw new Error('Missing health answers, insuree, or protection information');
    }

    const protectionSummary = buildProtectionSummary(protection);

    const pageObject: UnderwritingProfilePageObject = {
      id: underwritingProfile.id,
      policyNumber: underwritingProfile.policyNumber,
      archivedProposalId: underwritingProfile.archivedProposal.id,
      status: buildStatusObject(
        this.translate,
        underwritingProfile.status,
        underwritingProfile.archivedProposal.healthAnswersRevisions,
        underwritingProfile.extraRequirementsFile,
      ),
      underwriterId: underwritingProfile.underwriterId,
      underwriters: publicProfiles.map((profile) => ({
        agentId: profile.agentId,
        fullName: `${profile.firstName} ${profile.lastName}`,
      })),
      clientInfo: buildClientInfoObject(
        insuree,
        healthAnswers.birthday,
        healthAnswers.sex,
        underwritingProfile.archivedProposal.submittedAt,
        underwritingProfile.archivedProposal.hasPolicyReplacementNotice,
        underwritingProfile.extra?.salary,
      ),
      healthQuestionnaire: {
        questions: healthQuestionnaire.questions.filter(
          (q) => q.id !== QuestionId.SEX && q.id !== QuestionId.BIRTHDAY,
        ),
      },
      healthAnswers,
      extraQuestionnaireInfo: {
        extraQuestionnaires: healthQuestionnaire.extraQuestionnaires,
        canModifyIsIncludedInContract: true,
        canAnswerQuestions: false,
      },
      proposalNote: underwritingProfile.archivedProposal.extra?.note,
      requestableExtraQuestionnaires: requestableExtraQuestionnaire.extraQuestionnaires.map((eq) => ({
        ...eq,
        selected: false,
      })),
      profileInfo: {
        protectionSummary,
        hasMIBReplies:
          !!underwritingProfile.mibFile?.mibReplies && underwritingProfile.mibFile.mibReplies.length > 0,
        createdAt: underwritingProfile.createdAt,
      },
      surchargeSummary: {
        protectionSummary,
        clientFullName: `${insuree.firstName} ${insuree.lastName}`,
        policyNumber: underwritingProfile.policyNumber,
        languageCode: insuree.languageCode,
        protection,
        surchargedProtection:
          surchargedProtection && underwritingProfile.surchargeRate
            ? {
                ...surchargedProtection,
                surchargeRate: underwritingProfile.surchargeRate,
              }
            : undefined,
        surchargedProtectionSummary: surchargedProtection
          ? this.buildSurchargedProtectionSummary(protectionSummary, surchargedProtection)
          : undefined,
        proposalCompletionDate: underwritingProfile.archivedProposal.submittedAt,
      },
      extraRequirementsFile: underwritingProfile.extraRequirementsFile,
      notes: enrichedNotes,
    };

    pageObject.requestableExtraQuestionnaires = requestableExtraQuestionnaire.extraQuestionnaires.map(
      (eq) => ({
        ...eq,
        selected: [
          ...(healthQuestionnaire.extraQuestionnaires?.map((eq) => eq.id) || []),
          ...getPresetRevisions(underwritingProfile.archivedProposal.healthAnswersRevisions || []).flatMap(
            (ar) => Object.values(ar.questionnaires).map((q) => q.questionnaireId),
          ),
        ].includes(eq.id),
      }),
    );

    return pageObject;
  }

  private buildSurchargedProtectionSummary(
    initialProtectionSummary: ProtectionSummary,
    surchargedProtection: Protection,
  ): ProtectionSummary {
    const surchargedProtectionSummary = buildProtectionSummary(surchargedProtection);
    const riderTypes = new Set(surchargedProtectionSummary.riders?.map(({type}) => type));
    surchargedProtectionSummary.riders = initialProtectionSummary.riders?.map((r) => {
      if (!riderTypes.has(r.type)) {
        return {
          ...r,
          isRemoved: true,
        };
      }
      const surchargedRider = surchargedProtectionSummary.riders?.find((sr) => r.type === sr.type);
      if (!surchargedRider) {
        throw new Error(
          'A rider cannot appear in the surcharged protection but not in the initial protection.',
        );
      }
      return surchargedRider;
    });
    return surchargedProtectionSummary;
  }
}
